/**
 * MotionController — premium timelines for Personaliza.
 * Exposes window.STPersonalizaMotion
 */
(function (global) {
  'use strict';

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isMobile() {
    return window.matchMedia && window.matchMedia('(max-width: 719px)').matches;
  }

  function easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  function animate(duration, onUpdate, easing) {
    easing = easing || easeOutCubic;
    return new Promise((resolve) => {
      if (prefersReducedMotion() || duration <= 0) {
        onUpdate(1);
        resolve();
        return;
      }
      const start = performance.now();
      function frame(now) {
        const t = Math.min(1, (now - start) / duration);
        onUpdate(easing(t));
        if (t < 1) requestAnimationFrame(frame);
        else resolve();
      }
      requestAnimationFrame(frame);
    });
  }

  class MotionController {
    constructor(els) {
      this.els = els || {};
      this._toastTimer = null;
    }

    reduced() {
      return prefersReducedMotion();
    }

    async productTransition(stageEl, work) {
      if (!stageEl) {
        await work();
        return;
      }
      const ms = isMobile() ? 320 : 420;
      stageEl.classList.add('is-transitioning');
      if (this.reduced()) {
        await work();
        stageEl.classList.remove('is-transitioning');
        return;
      }
      await animate(ms * 0.45, (t) => {
        const o = 1 - t * 0.35;
        const s = 1 - t * 0.04;
        const y = t * 8;
        stageEl.style.opacity = String(o);
        stageEl.style.transform = `scale(${s}) translateY(${y}px)`;
      });
      await work();
      await animate(ms * 0.55, (t) => {
        const o = 0.65 + t * 0.35;
        const s = 0.96 + t * 0.04;
        const y = 8 * (1 - t);
        stageEl.style.opacity = String(o);
        stageEl.style.transform = `scale(${s}) translateY(${y}px)`;
      }, easeOutCubic);
      stageEl.style.opacity = '';
      stageEl.style.transform = '';
      stageEl.classList.remove('is-transitioning');
    }

    async colorCrossfade(stageEl, work) {
      if (!stageEl || this.reduced()) {
        await work();
        return;
      }
      stageEl.classList.add('is-color-fade');
      await animate(180, (t) => {
        stageEl.style.opacity = String(1 - t * 0.25);
        stageEl.style.transform = `scale(${1 - t * 0.015})`;
      });
      await work();
      await animate(220, (t) => {
        stageEl.style.opacity = String(0.75 + t * 0.25);
        stageEl.style.transform = `scale(${0.985 + t * 0.015})`;
      });
      stageEl.style.opacity = '';
      stageEl.style.transform = '';
      stageEl.classList.remove('is-color-fade');
    }

    /**
     * Upload → product apply animation.
     * floatCard: HTMLElement with img inside
     * mockup: ProductMockup instance
     * designImg: HTMLImageElement
     * srcUrl: string for float card
     */
    async applyDesign({ floatCard, floatImg, stageEl, mockup, designImg, srcUrl, onSettled }) {
      const total = isMobile() ? 750 : 1100;

      if (floatImg && srcUrl) floatImg.src = srcUrl;
      if (floatCard) {
        floatCard.hidden = false;
        floatCard.classList.add('is-visible');
        floatCard.style.opacity = '1';
        floatCard.style.transform = 'translate(-50%, -60%) scale(1)';
      }

      if (this.reduced()) {
        mockup.setDesign(designImg, mockup.fitDesignToPrintArea());
        mockup.setPulse(0);
        if (floatCard) {
          floatCard.hidden = true;
          floatCard.classList.remove('is-visible');
        }
        if (onSettled) onSettled();
        this.showSuccess();
        this.productFocus(stageEl);
        return;
      }

      stageEl?.classList.add('is-applying-design');

      // Phase 1: float visible (300–400ms)
      await animate(Math.min(400, total * 0.28), () => {});

      // Phase 2–4: move toward print area + scale down
      await animate(total * 0.45, (t) => {
        if (floatCard) {
          const y = -60 + t * 40;
          const s = 1 - t * 0.55;
          floatCard.style.opacity = String(1 - t * 0.85);
          floatCard.style.transform = `translate(-50%, ${y}%) scale(${s})`;
        }
        if (t > 0.55 && mockup.design !== designImg) {
          mockup.setDesign(designImg, { x: 0, y: 0, scale: 0.7 });
        }
      }, easeInOutCubic);

      // Lock into product
      mockup.fitDesignToPrintArea();
      mockup.setDesign(designImg, mockup.transform);

      // Phase 5: brightness pulse
      await animate(total * 0.18, (t) => {
        mockup.setPulse(Math.sin(t * Math.PI));
      });
      mockup.setPulse(0);

      if (floatCard) {
        floatCard.hidden = true;
        floatCard.classList.remove('is-visible');
        floatCard.style.opacity = '';
        floatCard.style.transform = '';
      }

      stageEl?.classList.remove('is-applying-design');
      this.showSuccess();
      await this.productFocus(stageEl);
      if (onSettled) onSettled();
    }

    async productFocus(stageEl) {
      if (!stageEl || this.reduced()) return;
      stageEl.classList.add('is-product-focus');
      await animate(280, (t) => {
        const s = 0.98 + t * 0.02;
        stageEl.style.transform = `scale(${s})`;
      });
      stageEl.style.transform = '';
      stageEl.classList.remove('is-product-focus');
    }

    showSuccess(message) {
      const el = this.els.success;
      if (!el) return;
      el.textContent = message || '✓ Diseño aplicado';
      el.hidden = false;
      el.classList.add('is-visible');
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => {
        el.classList.remove('is-visible');
        setTimeout(() => {
          el.hidden = true;
        }, 300);
      }, this.reduced() ? 1200 : 1800);
    }

    panelSwap(optionsEl, renderFn) {
      if (!optionsEl) {
        renderFn();
        return Promise.resolve();
      }
      if (this.reduced()) {
        renderFn();
        return Promise.resolve();
      }
      optionsEl.classList.add('is-swapping');
      return animate(120, (t) => {
        optionsEl.style.opacity = String(1 - t);
        optionsEl.style.transform = `translateY(${t * 4}px)`;
      }).then(() => {
        renderFn();
        return animate(180, (t) => {
          optionsEl.style.opacity = String(t);
          optionsEl.style.transform = `translateY(${(1 - t) * 4}px)`;
        });
      }).then(() => {
        optionsEl.style.opacity = '';
        optionsEl.style.transform = '';
        optionsEl.classList.remove('is-swapping');
      });
    }

    priceFlash(nodes) {
      (nodes || []).forEach((n) => {
        if (!n) return;
        n.classList.add('is-price-flash');
        setTimeout(() => n.classList.remove('is-price-flash'), 280);
      });
    }

    setButtonState(btn, state) {
      if (!btn) return;
      btn.classList.remove('is-loading', 'is-success');
      if (state === 'loading') {
        btn.classList.add('is-loading');
        btn.setAttribute('aria-busy', 'true');
        btn.disabled = true;
      } else if (state === 'success') {
        btn.classList.add('is-success');
        btn.setAttribute('aria-busy', 'false');
      } else {
        btn.removeAttribute('aria-busy');
      }
    }

    /** Idle 360 turntable — calls onTick() every interval until stopIdleSpin() */
    startIdleSpin(onTick, intervalMs) {
      this.stopIdleSpin();
      if (this.reduced()) return;
      const ms = intervalMs || (isMobile() ? 900 : 720);
      this._spinTimer = setInterval(() => {
        if (typeof onTick === 'function') onTick();
      }, ms);
    }

    stopIdleSpin() {
      if (this._spinTimer) {
        clearInterval(this._spinTimer);
        this._spinTimer = null;
      }
    }

    /** Crossfade frame change for turntable scrub */
    async frameCrossfade(stageEl, work) {
      if (!stageEl || this.reduced()) {
        await work();
        return;
      }
      stageEl.classList.add('is-spinning');
      await animate(90, (t) => {
        stageEl.style.opacity = String(1 - t * 0.12);
      });
      await work();
      await animate(110, (t) => {
        stageEl.style.opacity = String(0.88 + t * 0.12);
      });
      stageEl.style.opacity = '';
      stageEl.classList.remove('is-spinning');
    }
  }

  global.STPersonalizaMotion = { MotionController, prefersReducedMotion, animate, easeOutCubic };
})(typeof window !== 'undefined' ? window : globalThis);
